import { useCallback, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import {
  RECAPTCHA_ACTIONS,
  RECAPTCHA_TOKEN_MIN_LENGTH,
  RECAPTCHA_TOKEN_PATTERN,
} from "@/constants/recaptcha.constants";
import useToast from "@/contexts/toast-context";
import { RECAPTCHA_MESSAGES } from "@/messages/recaptcha/recaptcha.messages";
import { recaptchaService } from "@/services/recaptcha.service";
import { UseRecaptchaReturn } from "@/interfaces/recaptcha.interfaces";
import { shouldSkipRecaptchaVerification } from "@/utils/environment.utils";
export const useRecaptcha = (): UseRecaptchaReturn => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { showToast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const isValidTokenInput = useCallback(
    (token: string): boolean => {
      if (!token || token.trim() === "") {
        showToast({
          text: RECAPTCHA_MESSAGES.recaptchaError,
          type: "error",
        });
        return false;
      }
      return true;
    },
    [showToast]
  );

  const handleTokenGenerationError = useCallback(
    (error: unknown): void => {
      const errorMessage = error instanceof Error ? error.message : "";

      if (errorMessage.includes("network") || errorMessage.includes("timeout")) {
        showToast({
          text: RECAPTCHA_MESSAGES.recaptchaNetworkError,
          type: "error",
        });
      } else {
        showToast({
          text: RECAPTCHA_MESSAGES.recaptchaTokenGenerationFailed,
          type: "error",
        });
      }
    },
    [showToast]
  );

  const handleVerificationError = useCallback(
    (error: unknown): void => {
      const errorMessage = error instanceof Error ? error.message.toLowerCase() : "";

      if (errorMessage.includes("network") || errorMessage.includes("connection")) {
        showToast({
          text: RECAPTCHA_MESSAGES.recaptchaNetworkError,
          type: "error",
        });
      } else if (
        errorMessage.includes("authentication") ||
        errorMessage.includes("access denied")
      ) {
        showToast({
          text: RECAPTCHA_MESSAGES.recaptchaError,
          type: "error",
        });
      } else if (errorMessage.includes("too many")) {
        showToast({
          text: "Muitas tentativas de verificação. Aguarde um momento e tente novamente.",
          type: "error",
        });
      } else if (errorMessage.includes("score")) {
        showToast({
          text: RECAPTCHA_MESSAGES.recaptchaScoreTooLow,
          type: "error",
        });
      } else {
        showToast({
          text: RECAPTCHA_MESSAGES.recaptchaError,
          type: "error",
        });
      }
    },
    [showToast]
  );

  const executeRecaptchaAction = useCallback(
    async (action: string): Promise<string | null> => {
      if (!recaptchaService.isSiteKeyConfigured()) {
        showToast({
          text: RECAPTCHA_MESSAGES.recaptchaSiteKeyMissing,
          type: "error",
        });
        return null;
      }

      if (!executeRecaptcha) {
        showToast({
          text: RECAPTCHA_MESSAGES.recaptchaNotAvailable,
          type: "error",
        });
        return null;
      }

      setIsLoading(true);

      try {
        const token = await executeRecaptcha(action);

        if (!token) {
          throw new Error("Token generation returned null");
        }

        return token;
      } catch (error) {
        handleTokenGenerationError(error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [executeRecaptcha, showToast, handleTokenGenerationError]
  );

  const verifyRecaptchaToken = useCallback(
    async (recaptchaToken: string): Promise<boolean> => {
      if (!isValidTokenInput(recaptchaToken)) {
        return false;
      }

      if (recaptchaToken.length < RECAPTCHA_TOKEN_MIN_LENGTH) {
        showToast({ text: RECAPTCHA_MESSAGES.recaptchaInvalidToken, type: "error" });
        return false;
      }
      if (!RECAPTCHA_TOKEN_PATTERN.test(recaptchaToken)) {
        showToast({ text: RECAPTCHA_MESSAGES.recaptchaUnexpectedFormat, type: "error" });
        return false;
      }

      setIsLoading(true);

      try {
        const isValid = await recaptchaService.verifyToken(recaptchaToken);

        if (!isValid) {
          showToast({
            text: RECAPTCHA_MESSAGES.recaptchaVerificationFailed,
            type: "error",
          });
          return false;
        }

        return true;
      } catch (error) {
        handleVerificationError(error);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [showToast, isValidTokenInput, handleVerificationError]
  );

  return {
    executeRecaptchaAction,
    verifyRecaptchaToken,
    isLoading,
  };
};

/**
 * Hook simplificado para executar e verificar reCAPTCHA em uma única operação
 */
export const useRecaptchaVerification = () => {
  const { executeRecaptchaAction, verifyRecaptchaToken, isLoading } = useRecaptcha();

  const executeAndVerify = useCallback(
    async (action: string): Promise<boolean> => {
      if (shouldSkipRecaptchaVerification()) {
        return true;
      }

      const token = await executeRecaptchaAction(action);

      if (!token) {
        return false;
      }

      return await verifyRecaptchaToken(token);
    },
    [executeRecaptchaAction, verifyRecaptchaToken]
  );

  return {
    executeAndVerify,
    isLoading,
  };
};
